import moment from "moment";
import * as React from 'react';
import {OptionDifferenceDto, DifferenceDtoOfAssignedModeltypeBasicDto} from "./Client";
import {OptionDifferenceTableItem, MarketDiffValue, } from "../views/OptionDifferenceView/OptionDifferenceTable";
import {TDerivateIconProps, DerivateUtils} from "../components/Table/components/derivate/DerivateUtils";
import DataAdapterOptionsView from "../views/OptionsView/DataAdapterOptionsView";
import {Icon, FlagNameValues, Label, Flag, TextArea, Image} from "semantic-ui-react";
import {getWFStatusImageSrc, wfStatusRejectedImageSrc} from "../components/Table/components/workflow/WorkFlowState";

const noValueRenderKey: string = '-';

interface IOptionDifferenceTextItemProps {
    key: string;
    flag: FlagNameValues;
    text?: string;
}

const OptionDifferenceTextItem: React.StatelessComponent<IOptionDifferenceTextItemProps> = (props: IOptionDifferenceTextItemProps) => (
    <div className={"option-difference-text-item"}>
        <Label basic={true}>
            <Flag name={props.flag} />
        </Label>
        <TextArea
            key={props.key + '-' + props.flag}
            className="textDiffTextArea"
            fluid={true}
            value={props.text}
            disabled={true}
        />
    </div>
);

class OptionDifferenceService {

    public static excludedKeys: string[] = [
        'optionCode',
        'optionType'
    ];

    private static derivateNode = (value: string, name: any, size: any, color: any): JSX.Element => {
        return (
            <div className='cell-container'>
                <h4> <span> {value} </span> </h4>
                <Icon className={name === "close" ? "invisible" : ""} name={name} size={size} color={color} />
            </div>
        );
    }

    public static keyToLabel = (key: string): string => {
        switch (key) {
            case 'modeltypes':
                return 'DERIVATESTATES';
            case 'price':
                return 'PRICE';
            case 'availableStart':
                return 'AVAILABLESTART';
            case 'availableEnd':
                return 'AVAILABLEEND';
            case 'comment':
                return 'COMMENT';
            case 'constraint':
                return 'CONSTRAINT';
            case 'exclusion':
                return 'EXCLUSION';
            case 'packageContent':
                return 'PACKAGECONTENT';
            case 'reference':
                return 'REFERENCE';
            case 'referenceExc':
                return 'REFERENCEEXC';
            case 'intsequence':
                return 'INTSEQUENCE';
            case 'status':
                return 'STATUSVIEW';
            case 'shortText':
                return 'SHORTTEXT';
            case 'longText':
                return 'LONGTEXT';
            case 'footer':
                return 'FOOTER';
            case 'usp':
                return 'USP';
            default:
                return 'Not Defined';
        }
    }

    public static buildItems = (difference: OptionDifferenceDto): OptionDifferenceTableItem[] => {
        let items: OptionDifferenceTableItem[] = [];

        OptionDifferenceService.getKeys(difference).forEach((key: string) => {
            switch (key) {
                case 'modeltypes':
                    if (difference.modeltypes) {
                        difference.modeltypes.forEach((modeltype: DifferenceDtoOfAssignedModeltypeBasicDto, index: number) => {
                            let rowSpan: number = index === 0 && difference.modeltypes ? difference.modeltypes.length : 1;

                            const specificMarketAssignment = modeltype.differenceA ? DataAdapterOptionsView.getOptionState(modeltype.differenceA.assignmentType) : undefined;
                            const baseMarketAssignment = modeltype.differenceB ? DataAdapterOptionsView.getOptionState(modeltype.differenceB.assignmentType) : undefined;

                            const specificDerivateIcon: TDerivateIconProps | undefined = specificMarketAssignment ?
                                DerivateUtils.getDerivateAssignmentIconProps(specificMarketAssignment) : undefined;

                            const basicDerivateIcon: TDerivateIconProps | undefined = baseMarketAssignment ?
                                DerivateUtils.getDerivateAssignmentIconProps(baseMarketAssignment) : undefined;

                            const specificDerivateNode = specificDerivateIcon ? OptionDifferenceService.derivateNode(
                                modeltype.differenceA ? DataAdapterOptionsView.createDerivateName(modeltype.differenceA.modeltypeBasic) : '',
                                specificDerivateIcon.name,
                                specificDerivateIcon.size,
                                specificDerivateIcon.color
                            ) : undefined;

                            const basicDerivateNode = basicDerivateIcon ? OptionDifferenceService.derivateNode(
                                modeltype.differenceB ? DataAdapterOptionsView.createDerivateName(modeltype.differenceB.modeltypeBasic) : '',
                                basicDerivateIcon.name,
                                basicDerivateIcon.size,
                                basicDerivateIcon.color
                            ) : undefined;

                            let diffDerivateValue: MarketDiffValue = {
                                specificMarketValue: specificDerivateNode ? specificDerivateNode : noValueRenderKey,
                                baseMarketValue: basicDerivateNode ? basicDerivateNode : noValueRenderKey
                            };

                            let item: OptionDifferenceTableItem = {
                                key: key,
                                label: index === 0 ? OptionDifferenceService.keyToLabel(key) : undefined,
                                labelRowSpan: rowSpan,
                                diffValues: [diffDerivateValue]
                            };

                            items.push(item);
                        });
                    }
                    break;

                case 'availableStart':
                case 'availableEnd':
                    let diff = difference[key];
                    let diffDateValue: MarketDiffValue = {
                        specificMarketValue: diff && diff.differenceA ? moment(diff.differenceA).format('DD.MM.YYYY') : noValueRenderKey,
                        baseMarketValue: diff && diff.differenceB ? moment(diff.differenceB).format('DD.MM.YYYY') : noValueRenderKey
                    };

                    items.push({
                        key: key,
                        label: OptionDifferenceService.keyToLabel(key),
                        labelRowSpan: 1,
                        diffValues: [diffDateValue]
                    });
                    break;
                case 'price':
                    let pricediff = difference[key];
                    let diffPriceValue: MarketDiffValue = {
                        specificMarketValue: pricediff && pricediff.differenceA ? pricediff.differenceA / 100 + '$' : noValueRenderKey,
                        baseMarketValue: pricediff && pricediff.differenceB ? pricediff.differenceB / 100 + '$' : noValueRenderKey
                    };

                    items.push({
                        key: key,
                        label: OptionDifferenceService.keyToLabel(key),
                        labelRowSpan: 1,
                        diffValues: [diffPriceValue]
                    });
                    break;
                case 'constraint':
                case 'exclusion':
                case 'packageContent':
                    let cediff = difference[key];
                    let diffRestrictionValue: MarketDiffValue = {
                        specificMarketValue: cediff && cediff.differenceA ? cediff.differenceA.join(', ') : noValueRenderKey,
                        baseMarketValue: cediff && cediff.differenceB ? cediff.differenceB.join(', ') : noValueRenderKey
                    };

                    items.push({
                        key: key,
                        label: OptionDifferenceService.keyToLabel(key),
                        labelRowSpan: 1,
                        diffValues: [diffRestrictionValue]
                    });
                    break;
                case 'shortText':
                case 'longText':
                case 'footer':
                case 'usp':
                    let textdiff = difference[key];
                    if (textdiff && (textdiff.enText || textdiff.deText)) {
                        let diffValues: MarketDiffValue[] = [];

                        if (textdiff && textdiff.deText) {
                            let diffDeTextValue: MarketDiffValue = {
                                specificMarketValue: (
                                    <OptionDifferenceTextItem
                                        key={key + '-SMV'}
                                        flag={"de"}
                                        text={textdiff && textdiff.deText && textdiff.deText.differenceA}
                                    />
                                ),
                                baseMarketValue: (
                                    <OptionDifferenceTextItem
                                        key={key + '-BMV'}
                                        flag={"de"}
                                        text={textdiff && textdiff.deText && textdiff.deText.differenceB}
                                    />
                                )
                            };

                            diffValues.push(diffDeTextValue);
                        }

                        if (textdiff && textdiff.enText) {
                            let diffEnTextValue: MarketDiffValue = {
                                specificMarketValue: (
                                    <OptionDifferenceTextItem
                                        key={key + '-SMV'}
                                        flag={"gb"}
                                        text={textdiff && textdiff.enText && textdiff.enText.differenceA}
                                    />
                                ),
                                baseMarketValue: (
                                    <OptionDifferenceTextItem
                                        key={key + '-BMV'}
                                        flag={"gb"}
                                        text={textdiff && textdiff.enText && textdiff.enText.differenceB}
                                    />
                                )
                            };

                            diffValues.push(diffEnTextValue);
                        }

                        items.push({
                            key: key,
                            label: OptionDifferenceService.keyToLabel(key),
                            labelRowSpan: diffValues.length,
                            diffValues: diffValues
                        });
                    }
                    break;
                case 'status':
                    if (difference.status) {
                        let diffStatusValue: MarketDiffValue = {
                            specificMarketValue: difference.status.differenceA ?
                                (
                                    <>
                                        {difference.status.differenceA.name}
                                        {difference.status.differenceA.rejected &&
                                        <p className={"workflow-state-reject-reason"}>
                                            <Image
                                                src={wfStatusRejectedImageSrc}
                                                width={15}
                                                height={15}
                                                inline={true}
                                                style={{marginRight: 5}}
                                            />
                                            {difference.status.differenceA.rejectMessage}
                                        </p>
                                        }
                                        <Image
                                            src={getWFStatusImageSrc(difference.status.differenceA.type)}
                                            size={'mini'}
                                        />
                                    </>
                                )
                                : noValueRenderKey,
                            baseMarketValue: difference.status.differenceB ?
                                (
                                    <>
                                        {difference.status.differenceB.name}
                                        {difference.status.differenceB.rejected &&
                                        <p className={"workflow-state-reject-reason"}>
                                            <Image
                                                src={wfStatusRejectedImageSrc}
                                                width={15}
                                                height={15}
                                                inline={true}
                                                style={{marginRight: 5}}
                                            />
                                            {difference.status.differenceB.rejectMessage}
                                        </p>
                                        }
                                        <Image
                                            src={getWFStatusImageSrc(difference.status.differenceB.type)}
                                            size={'mini'}
                                        />
                                    </>
                                )
                                : noValueRenderKey,
                        };

                        items.push({
                            key: key,
                            label: OptionDifferenceService.keyToLabel(key),
                            labelRowSpan: 1,
                            diffValues: [diffStatusValue]
                        });
                    }
                    break;
                default:
                    let diffUndefinedFieldValue: MarketDiffValue = {
                        specificMarketValue: difference[key].differenceA ? difference[key].differenceA : noValueRenderKey,
                        baseMarketValue: difference[key].differenceB ? difference[key].differenceB : noValueRenderKey
                    };

                    items.push({
                        key: key,
                        label: OptionDifferenceService.keyToLabel(key),
                        labelRowSpan: 1,
                        diffValues: [diffUndefinedFieldValue]
                    });
                    break;
            }
        });

        return items;
    }

    public static getKeys = (difference: OptionDifferenceDto): string[] => {
        let keys: string[] = Object.keys(difference).filter((key: string) => {
            return !OptionDifferenceService.excludedKeys.find((excludedKey: string) =>
                excludedKey.toLowerCase().trim() === key.toLowerCase().trim());
        });

        return keys;
    }

}

export default OptionDifferenceService;



// WEBPACK FOOTER //
// ./src/services/OptionDifferenceService.tsx